import { Typography } from "@/components";
import useProfile from "@/hooks/useProfile";
import { getStatusColor } from "@/libraries/colors";
import { QUERY_KEYS } from "@/libraries/constant";
import { readableHumanDate } from "@/libraries/dayjs-relative";
import { randInt } from "@/libraries/helper";
import { getCaseInfos } from "@/queries";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Divider from "@mui/material/Divider";
import { onlineManager, useSuspenseInfiniteQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import dayjs from "dayjs";
import flatMap from "lodash/flatMap";
import groupBy from "lodash/groupBy";
import orderBy from "lodash/orderBy";
import { useMemo, useRef } from "react";
import { VList, type VListHandle } from "virtua";

export const Route = createFileRoute(
	"/dashboard/case/view/$id/case-info/$type/",
)({
	loader: ({ context, params }) => {
		if (onlineManager.isOnline() && !context.isRestoring) {
			return context.queryClient.ensureInfiniteQueryData(
				getCaseInfos({
					caseId: params.id,
					info_type: params.type,
				}),
			);
		}
		return context.queryClient.getQueryData([
			QUERY_KEYS.GET_CASE_INFOS,
			{ caseId: params.id, info_type: params.type },
		]);
	},
	component: RouteComponent,
});

function RouteComponent() {
	const scrollRef = useRef<VListHandle>(null);
	const caseId = Route.useParams({
		select: (s) => s.id,
	});
	const summaryType = Route.useParams({
		select: (s) => s.type,
	});
	const summariesQuery = useSuspenseInfiniteQuery(
		getCaseInfos({ caseId, info_type: summaryType }),
	);
	const { profile } = useProfile();
	const caseInfos = useMemo(() => {
		if (!summariesQuery.data.length) return [];
		const sortCaseInfos = orderBy(summariesQuery.data, ["created_at"], ["asc"]);
		const groupedCaseInfos = groupBy(sortCaseInfos, (item) =>
			dayjs(item.created_at).format("YYYY-MM-DD"),
		);
		return flatMap(groupedCaseInfos, (items, date) => [
			{
				type: "date" as const,
				id: randInt(),
				value: dayjs(date, "YYYY-MM-DD").format("DD MMM YYYY"),
			},
			...items.map((item) => ({
				type: "item" as const,
				id: item.id,
				value: item,
			})),
		]);
	}, [summariesQuery.data]);
	return (
		<div className="relative flex-1 flex flex-col overflow-y-auto">
			<VList ref={scrollRef} className="flex-1" reverse shift>
				{caseInfos.map((caseInfo) =>
					caseInfo.type === "date" ? (
						<Divider key={caseInfo.id}>
							<Typography fontWeight={500} fontSize={12} className="py-2">
								{caseInfo.value}
							</Typography>
						</Divider>
					) : (
						<div key={caseInfo.id} className="relative container py-2">
							<Card variant="outlined" className="!rounded-xl !p-4">
								<CardHeader
									action={
										<div className="flex items-center gap-2">
											<Typography fontWeight={400} fontSize={10}>
												{readableHumanDate(caseInfo.value.updated_at)}
											</Typography>
											<DoneAllIcon className="!text-base text-green-500" />
										</div>
									}
									title={
										<div className="flex flex-1 gap-2 items-center">
											<Typography fontWeight={700} fontSize={14}>
												{[
													caseInfo.value.user.fullname,
													profile?.id === caseInfo.value.user.id ? " (Me)" : "",
												].join("")}
											</Typography>
											<span className="relative flex h-2 w-2">
												<span
													className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75"
													style={{
														backgroundColor: getStatusColor(
															caseInfo.value.user.status || "available",
														),
													}}
												/>
												<span
													className="relative inline-flex rounded-full h-2 w-2"
													style={{
														backgroundColor: getStatusColor(
															caseInfo.value.user.status || "available",
														),
													}}
												/>
											</span>
										</div>
									}
									className="!p-0"
									classes={{
										title: "!font-medium !text-sm",
									}}
								/>
								<CardContent className="!p-0">
									<Typography
										fontWeight={400}
										fontSize={14}
										className="whitespace-pre-line"
									>
										{caseInfo.value.description}
									</Typography>
								</CardContent>
							</Card>
						</div>
					),
				)}
			</VList>
		</div>
	);
}
